import React, { useCallback, useEffect } from 'react';

// This is a workaround for missing CSSAnimation type from TS definitions 4.3.5
interface CSSAnimation extends Animation {
  animationName: string;
}

interface IUseAnimationSyncProps {
  animatedElementRef: React.MutableRefObject<HTMLElement | null>;
  animatedGroupClassName: string;
  animationName: string;
}

export const useAnimationSync = ({
  animatedElementRef,
  animatedGroupClassName,
  animationName,
}: IUseAnimationSyncProps): void => {
  const animationStartCallback = useCallback(
    (event: AnimationEvent) => {
      // check if a proper animation is to be synchronised
      if (event.animationName !== animationName) return;

      const thisAnimatedElement = event.target;

      // get other animated elements from the group
      const allAnimatedElementsInGroup = Array.from(document.getElementsByClassName(animatedGroupClassName));
      const otherAnimatedElementsInGroup = allAnimatedElementsInGroup.filter(
        element => element !== thisAnimatedElement,
      );

      if (!thisAnimatedElement || !(thisAnimatedElement instanceof Element)) return;

      // find correct animation on elements
      const thisAnimation = findAnimationByName([thisAnimatedElement], animationName);
      const otherRunningAnnimation = findAnimationByName(otherAnimatedElementsInGroup, animationName);

      if (!thisAnimation || !otherRunningAnnimation) return;

      // sync up start time of this animation
      thisAnimation.startTime = otherRunningAnnimation.startTime;
    },
    [animatedGroupClassName, animationName],
  );

  useEffect(() => {
    const animatedElement = animatedElementRef.current;
    if (!animatedElement) return;

    animatedElement.addEventListener('animationstart', animationStartCallback);

    return () => {
      animatedElement.removeEventListener('animationstart', animationStartCallback);
    };
  }, [animatedElementRef, animationStartCallback]);
};

const isCssAnimation = (animation: unknown): animation is CSSAnimation =>
  typeof animation === 'object' && !!animation && 'animationName' in animation;

const findAnimationByName = (elements: Element[], name: string): CSSAnimation | undefined => {
  for (const el of elements) {
    const animations = el.getAnimations();
    const correctAnimation = animations.filter(isCssAnimation).find(animation => animation.animationName === name);

    if (correctAnimation) return correctAnimation;
  }

  return;
};
